/**
 * Helper format tanggal untuk form dan payload API.
 *
 * Kontrak aplikasi: tanggal lahir dan tanggal wafat disimpan di state form,
 * ditampilkan di UI, dan dikirim ke API dalam format DD-MM-YYYY.
 * API dapat mengembalikan format ISO (YYYY-MM-DD atau YYYY-MM-DDTHH:mm:ssZ),
 * sehingga setiap nilai dari server dinormalisasi lewat `toDisplayDate`.
 */

/** Token format untuk react-datepicker (date-fns), bukan teks yang dilihat user. */
export const DATE_PICKER_FORMAT = "dd-MM-yyyy";

/** Placeholder yang ditampilkan ke user. */
export const DATE_PLACEHOLDER = "DD-MM-YYYY";

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const DISPLAY_DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;

const padTwoDigits = (value) => String(value).padStart(2, "0");

/**
 * Ubah nilai tanggal (objek Date, string ISO, atau string DD-MM-YYYY)
 * menjadi string DD-MM-YYYY. Mengembalikan "" bila kosong atau tidak valid.
 *
 * @param {Date|string|null|undefined} value
 * @returns {string}
 */
export const toDisplayDate = (value) => {
  if (!value) return "";

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    // Pakai komponen tanggal lokal agar tidak bergeser satu hari karena timezone.
    const day = padTwoDigits(value.getDate());
    const month = padTwoDigits(value.getMonth() + 1);
    return `${day}-${month}-${value.getFullYear()}`;
  }

  const stringValue = String(value);

  if (DISPLAY_DATE_PATTERN.test(stringValue)) return stringValue;

  const isoMatch = stringValue.match(ISO_DATE_PATTERN);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}-${month}-${year}`;
  }

  return "";
};

/**
 * Kebalikan dari `toDisplayDate`: string DD-MM-YYYY menjadi objek Date lokal.
 * Dipakai react-datepicker untuk menyorot tanggal terpilih saat kalender dibuka.
 * Mengembalikan null bila nilai kosong atau tanggalnya tidak valid.
 *
 * @param {string|null|undefined} value
 * @returns {Date|null}
 */
export const fromDisplayDate = (value) => {
  if (!value) return null;

  const match = String(value).match(DISPLAY_DATE_PATTERN);
  if (!match) return null;

  const [, day, month, year] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));

  // Tolak tanggal yang di-rollover oleh Date, contoh 31-02-2000 menjadi 02-03-2000.
  const isRolledOver =
    parsed.getDate() !== Number(day) || parsed.getMonth() !== Number(month) - 1;

  return isRolledOver ? null : parsed;
};
